<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-6">Post generation queue config</h1>
        <v-card>
          <v-card-title>Configuration de la file de génération</v-card-title>
          <v-card-text>
            <p class="text--secondary mb-4">
              Cette politique protège
              <code>aif_process_next_post_generation</code>
              contre les pics URL Batch et les démarrages groupés de
              publications futures. Les débits sont espacés régulièrement dans
              le temps.
            </p>

            <v-alert v-if="loadError" type="error" dense outlined>
              La configuration n'a pas pu être chargée. Les valeurs affichées
              ne peuvent pas être modifiées tant que le chargement n'a pas
              réussi.
            </v-alert>

            <v-form ref="form" v-model="formValid">
              <div
                v-for="section in sections"
                :key="section.title"
                class="mb-5"
              >
                <h3 class="subtitle-1 mb-1">{{ section.title }}</h3>
                <p class="caption text--secondary mb-3">
                  {{ section.description }}
                </p>
                <v-row>
                  <v-col
                    v-for="field in section.fields"
                    :key="field.key"
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      v-model.number="config[field.key]"
                      type="number"
                      :label="field.label"
                      :rules="fieldRules(field)"
                      :min="field.min"
                      :max="field.max"
                      :disabled="loading || !loaded"
                      outlined
                      dense
                      :hint="field.hint"
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </div>

              <v-alert v-if="crossValidationError" type="error" dense outlined>
                {{ crossValidationError }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              v-if="loadError"
              text
              color="primary"
              :loading="loading"
              :disabled="saving"
              @click="load"
            >
              Réessayer
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :disabled="
                loading ||
                !loaded ||
                !dirty ||
                !formValid ||
                !!crossValidationError ||
                saving
              "
              :loading="saving"
              @click="save"
            >
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "@axios";
import store from "@/store";

export default {
  name: "QueueConfig",
  data() {
    return {
      config: {
        max_processing: 23,
        url_batch_max_processing: 5,
        future_max_processing: 8,
        url_batch_starts_per_minute: 3,
        future_starts_per_minute: 5,
        future_lookahead_hours: 48,
        urgent_within_minutes: 120,
        future_night_start_hour: 20,
        future_night_end_hour: 7,
      },
      initial: null,
      loading: false,
      loaded: false,
      loadError: false,
      saving: false,
      formValid: true,
      sections: [
        {
          title: "Capacité simultanée",
          description:
            "Les allocations inutilisées restent disponibles aux autres demandes. Les futurs urgents peuvent dépasser l'allocation future, mais jamais le maximum global ni le plafond URL Batch. 0 suspend la file ou la catégorie concernée.",
          fields: [
            {
              key: "max_processing",
              label: "Maximum global",
              min: 0,
              max: 100,
              hint: "Limite de sécurité de toute la file",
            },
            {
              key: "url_batch_max_processing",
              label: "Maximum URL Batch",
              min: 0,
              max: 100,
              hint: "Allocation souple et plafond absolu",
            },
            {
              key: "future_max_processing",
              label: "Allocation futurs standard",
              min: 0,
              max: 100,
              hint: "Plafond des futurs non urgents",
            },
          ],
        },
        {
          title: "Débit de démarrage",
          description:
            "Les départs sont espacés uniformément : 3/minute correspond à un départ toutes les 20 secondes.",
          fields: [
            {
              key: "url_batch_starts_per_minute",
              label: "Départs URL Batch/minute",
              min: 1,
              max: 60,
              hint: "Entre 1 et 60",
            },
            {
              key: "future_starts_per_minute",
              label: "Départs futurs/minute",
              min: 1,
              max: 60,
              hint: "Entre 1 et 60",
            },
          ],
        },
        {
          title: "Planification future",
          description:
            "Heures exprimées dans le fuseau Europe/Paris. La fin de nuit est exclusive.",
          fields: [
            {
              key: "future_lookahead_hours",
              label: "Anticipation future (heures)",
              min: 1,
              max: 744,
              hint: "Horizon maximum de génération",
            },
            {
              key: "urgent_within_minutes",
              label: "Priorité proche (minutes)",
              min: 0,
              max: 1440,
              hint: "Peut utiliser la capacité générale",
            },
            {
              key: "future_night_start_hour",
              label: "Début de nuit (heure)",
              min: 0,
              max: 23,
              hint: "Défaut : 20 h",
            },
            {
              key: "future_night_end_hour",
              label: "Fin de nuit (heure)",
              min: 0,
              max: 23,
              hint: "Défaut : 7 h, valeur exclusive",
            },
          ],
        },
      ],
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.config) !== JSON.stringify(this.initial);
    },
    crossValidationError() {
      if (
        Number(this.config.future_night_start_hour) ===
        Number(this.config.future_night_end_hour)
      ) {
        return "Les heures de début et de fin de nuit doivent différer.";
      }

      const globalLimit = Number(this.config.max_processing);
      if (globalLimit > 0) {
        if (Number(this.config.url_batch_max_processing) > globalLimit) {
          return "Le maximum URL Batch ne peut pas dépasser le maximum global.";
        }
        if (Number(this.config.future_max_processing) > globalLimit) {
          return "L'allocation des futurs ne peut pas dépasser le maximum global.";
        }
      }
      return "";
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    fieldRules(field) {
      return [
        (value) =>
          (value !== "" && value !== null && value !== undefined) ||
          "Valeur requise",
        (value) => Number.isInteger(Number(value)) || "Doit être un entier",
        (value) => Number(value) >= field.min || `Minimum : ${field.min}`,
        (value) => Number(value) <= field.max || `Maximum : ${field.max}`,
      ];
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("/aif/queue-config/");
        this.config = { ...data };
        this.initial = { ...data };
        this.loaded = true;
        this.loadError = false;
      } catch (e) {
        this.loaded = false;
        this.loadError = true;
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur au chargement de la configuration.",
          color: "error",
          timeout: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!this.loaded || !this.formValid || this.crossValidationError) return;
      this.saving = true;
      try {
        const payload = Object.fromEntries(
          Object.entries(this.config).map(([key, value]) => [
            key,
            Number(value),
          ])
        );
        const { data } = await axios.put("/aif/queue-config/", payload);
        this.config = { ...data };
        this.initial = { ...data };
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: "Configuration enregistrée.",
          color: "success",
          timeout: 3000,
        });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        await store.dispatch("app/setSnackBar", {
          show: true,
          text:
            typeof detail === "string" ? detail : "Erreur à l'enregistrement.",
          color: "error",
          timeout: 3000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
